
import Axios, { AxiosResponse } from "axios";
import { EventEmitter } from "events";
import { AssetInfoTypes, Configuration, IMaterial, MaterialTypes } from "threngine";
import { Controller3D } from "../3DController";
import { ConfiguratorModel } from "../ConfiguratorModel";
import { FileLoadProgress } from "./FileLoadProgress";
import { RenderEngine } from "../RenderEngine";
import { getWhiteLabel } from "../../../whitelabel";
import { translate } from "../translateController";
import { EventController } from "../Event/EventController";
import { Vector3, Object3D, Box3 } from "three";
import { doModelLoadIntroAnimation } from "./ModelLoadIntroAnimation";

export interface CarModelGroupings {
  carpaint?: string;
  background: string;
  wheels: string;
  lights: string;
  welcomeScreen: string;
}

export enum CarModelCameraList {
  Orbit = "orbit",
  Wheel = "wheel",
  IntermediateSeatFrontLeft = "intermediateSeatFL",
  IntermediateSeatFrontRight = "intermediateSeatFR",
  IntermediateSeatRearLeft = "intermediateSeatRL",
  IntermediateSeatRearRight = "intermediateSeatRR",
  ExitInteriorLeft = "exitInteriorL",
  ExitInteriorRight = "exitInteriorR",
  SeatFL = "seatFL",
  SeatFR = "seatFR",
  SeatRL = "seatRL",
  SeatRR = "seatRR",
  GroundHotspotLights = "ghLights",
  GroundHotspotDoorsLeft = "ghDoorsLeft",
  GroundHotspotDoorsRight = "ghDoorsRight",
  GroundHotspotTrunk = "ghTrunk",
  TrunkLoading = "trunkLoading",
  PassengerStanding = "passengerStanding",
  PassengerIntDriver = "passengerIntDriver",
  PassengerIntDriverInOut = "passengerIntDriverInOut",
  PassengerIntFL = "passengerIntFL",
  PassengerIntRL = "passengerIntRL",
  PassengerIntRR = "passengerIntRR",
  PassengerIntFR = "passengerIntFR",
  XRayCar = "xrayCar",
  XRaySeat = "xraySeat",
  XRayTrunk = "xrayTrunk",
  XRayBattery = "xrayBattery"
}

export interface CarModelCameraInfo {
  state: boolean;
  desktop: string;
  mobile?: string;
  time?: number;
}

export interface CarModelAnimations {
  doorFL: string[];
  doorFR: string[];
  doorRL: string[];
  doorRR: string[];
  trunkLid: string[];
  sunRoof: string[];
  cupHolder: string[];
  airbags: string[];
}

export interface CarModelGroundHotspotTrigger {
  trigger: string;
  object: string;
}

export interface CarModelTriggers {
  carpaint: string[];
  interiorColor: string[];
  lights: string[];
  wheels: string[];
  doorFL: string[];
  doorFR: string[];
  doorRL: string[];
  doorRR: string[];
  trunkLid: string[];
  seatFL: string[];
  seatFR: string[];
  seatRL: string[];
  seatRR: string[];
  sunRoof: string[];
  welcomeScreen: string;
  cupHolder: string;
  exitInterior: string[];
  ghLights: CarModelGroundHotspotTrigger;
  ghDoorsLeft: CarModelGroundHotspotTrigger;
  ghDoorsRight: CarModelGroundHotspotTrigger;
  ghTrunk: CarModelGroundHotspotTrigger;
  plate: string[];
}

export interface CarModelHeadlineHotspotInfo {
  name: string;
  label: string;
  description?: string;
}

export interface CarModelInteractionHotspotInfo {
  name: string;
}

export interface CarModelHotspots {
  exterior: string[];
  interior: string[];
  visibility: any;
  headline: CarModelHeadlineHotspotInfo[];
  interaction: CarModelInteractionHotspotInfo[];
}

export interface CarModelDrivingCamera {
  desktop: string;
  mobile?: string;
  time: number;
  easing: string;
}

export interface CarModelDrivingAnimation {
  accelerationTime: number;
  maxVelocity: number;
  brakeDecay: number;
  wheels: string[];
  initialCameraTime: number;
  userCameras: CarModelDrivingCamera[];
  presentationCameras: CarModelDrivingCamera[];
}

export interface CarModelAR {
  excludedMeshes: string[];
  disclaimerMesh: string;
  materialOverrideMeshes: string[];
}

export interface CarModelAmbilight {
  materials: string[];
  colors: string[];
}

export interface CarDefaultState {
  interior: string[];
  exterior?: {
    default?:string;
    entries:string[];
  };
}

export interface CarTrunkLoading {
  boxes?: {
    visibilityGroup: string;
    animationRoot: string;
    boxRootS: string;
    boxRootM: string;
    boxRootL: string;
    trigger: string;
  },
  shopping?: {
    visibilityGroup: string;
    animationRoot: string;
    bottleRoot: string;
    bagRoot: string;
    trigger: string;
  },
  travelling?: {
    visibilityGroup: string;
    animationRoot: string;
    loadRoot: string;
    trigger: string;
  }
  unloadTrigger: string;
}

export interface CarXRay {
  enabled?: string[];
  translucentNodes:string[];
  hiddenNodes:string[];
}

export interface CarLightOrchestra {
  duration: number,
  keys:{
    meshes:string[],
    time:number
  }[];
}

export interface CarModelConfig {
  scene: string;
  groupings: CarModelGroupings;
  defaultState: CarDefaultState;
  ambilight?: CarModelAmbilight;
  cameras: { [key: string]: CarModelCameraInfo };
  animations: CarModelAnimations;
  trigger: CarModelTriggers;
  hotspots: CarModelHotspots;
  driving: CarModelDrivingAnimation;
  trunkLoading: CarTrunkLoading;
  xray?: CarXRay;
  ar: CarModelAR;
  lightOrchestra?:CarLightOrchestra;
  backgrounds?:{
    paint:string;
    background:string;
  }[];
  features:{
    airbag:boolean;
    haveBackDoors:boolean;
    haveBackSeats:boolean;
  };
};

export const nameMap = [
  {
    name: "Taos",
    car: "taos",
    disclaimer: "La versión del vehículo mostrado es un Taos Highline año modelo 2022. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/Selecciona%20una%20Concesionaria/31110/30475/highline/CL14LY/2021/1/+/V7SCY45T/+/+/+/23.9108313666701/-101.82614999999998/4?sourceID=ihdcc",
    exception: [
      // Old (default blue) Taos exceptions
      // { ex: "84d1f2b5", int: "Interior Accent Brown" },
      // { ex: "5eb98a5f1f1d8000160fbc76", int: "Interior Accent Brown" },
      // { ex: "5eb911231f1d8000160fbc3e", int: "Interior Accent Grey" },
      // { ex: "cea81834", int: "Interior Accent Grey" },
      // { ex: "5eb9112a1f1d8000160fbc40", int: "Interior Accent Grey" },
      // { ex: "5eb911011f1d8000160fbc36", int: "Interior Accent Grey" }

      // New (default red) Taos exceptions
      { ex: "wyf5mjqd" /* Rojo Kings   */, int: "Interior Accent Brown" },
      { ex: "cea81834" /* Gris Platino */, int: "Interior Accent Grey"  },
      { ex: "dbdef5fa" /* Plata Pirita */, int: "Interior Accent Grey"  },
      { ex: "5eb911011f1d8000160fbc36" /* Blanco Puro*/, int: "Interior Accent Grey" }
    ]
  },
  {
    name: "Cross Sport MY21",
    car: "crosssport-my21",
    disclaimer: "La versión del vehículo mostrado es un Nuevo Cross Sport R-Line año modelo 2021. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/VSK3U4X4?sourceID=ihdcc",
    exception: [
      { ex: "dbdef5fa", int: "Interior Accent Dark Burgundy and Titan Black" }
    ]
  },
  {
    name: "Cross Sport",
    car: "crosssport",
    disclaimer: "La versión del vehículo mostrado es un Cross Sport R-Line año modelo 2022. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/VSK3U4X4?sourceID=ihdcc",
    exception: [
      { ex: "dbdef5fa", int: "Interior Accent Dark Burgundy and Titan Black" }
    ]
  },
  {
    name: "Tiguan",
    car: "tiguan",
    disclaimer: "La versión del vehículo mostrado es un Tiguan Highline año modelo 2021. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/Selecciona%20una%20Concesionaria/31120/30489/highline/BW24LT/2021/1/+/VODPM6E3/+/+/+/23.9108313666701/-101.82614999999998/5?sourceID=ihdcc",
    exception: [
      { ex: "7854a873", int: "Interior Accent Saffrano and Black" }
    ]
  },
  {
    name: "Nuevo Tiguan",
    car: "nuevotiguan",
    disclaimer: "La versión del vehículo mostrado es un Tiguan R-Line año modelo 2022. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/Selecciona%20una%20Concesionaria/31120/30489/highline/BW24LT/2021/1/+/VODPM6E3/+/+/+/23.9108313666701/-101.82614999999998/5?sourceID=ihdcc",
    exception: [
      { ex: "7854a873", int: "Interior Accent Saffrano and Black" }
    ]
  },
  {
    name: "Jetta",
    car: "jetta-my21",
    disclaimer: "La versión del vehículo mostrado es un Jetta Highline año modelo 2021. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/Selecciona%20un%20Modelo/30904/+/+/+/+/+/carline/+/+/+/+/+/+/+",
    exception: [
    ]
  },
  {
    name: "Nuevo Jetta",
    car: "jetta",
    disclaimer: "La versión del vehículo mostrado es un Nuevo Jetta Sportline año modelo 2022. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/Selecciona%20un%20Modelo/30904/+/+/+/+/+/carline/+/+/+/+/+/+/+",
    exception: [
    ]
  },
  {
    name: "T-Cross",
    car: "tcross-my22",
    disclaimer: "La versión del vehículo mostrado es un T-Cross Highline año modelo 2022. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/Selecciona%20una%20Concesionaria/30252/30417/highline/BF14T3-MSNRSM9/2021/2/+/VUWSXMOL/+/+/+/23.9108313666701/-101.82614999999998/5?sourceID=ihdcc",
    exception: [
    ]
  },
  {
    name: "Nuevo T-Cross",
    car: "tcross",
    disclaimer: "La versión del vehículo mostrado es un Nuevo T-Cross Highline año modelo 2022. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "",
    exception: [
    ]
  },
  {
    name: "Teramont",
    car: "teramont",
    disclaimer: "La versión del vehículo mostrado es un Teramont Highline año modelo 2021. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/Selecciona%20una%20Concesionaria/31657/+/+/+/+/+/carline/+/+/+/+/23.9108313666701/-101.82614999999998/5",
    exception: [
      { ex: "dbdef5fa", int: "Cinnamon and Titan Black" },
      { ex: "cea81834", int: "Cinnamon and Titan Black" }
    ]
  },
  {
    name: "Polo",
    line: "",
    car: "polo",
    disclaimer: "La versión del vehículo mostrado es un Polo Comfortline Plus año modelo 2021. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/Selecciona%20un%20Modelo/30205/+/+/+/+/+/carline/+/+/+/+/+/+/+",
    exception: [
    ]
  },
  {
    name: "Virtus",
    line: "",
    car: "virtus",
    disclaimer: "La versión del vehículo mostrado es un Virtus Tiptronic año modelo 2022. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/app/autos-registro/vw-mx/contactanos/es/Selecciona%20un%20Modelo/30290/+/+/+/+/+/carline/+/+/+/+/+/+/+",
    exception: [
    ]
  },
  {
    name: "Nivus",
    line: "",
    car: "nivus",
    disclaimer: "La versión del vehículo mostrado es un Nuevo Nivus Highline año modelo 2022. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/es/autos-suvs/nivus.html",
    exception: [
    ]
  },
  {
    name: "Saveiro",
    line: "",
    car: "saveiro",
    disclaimer: "La versión del vehículo mostrado es un Saveiro Robust año modelo 2022. Para consultar las versiones, precios, equipamiento y colores de tu vehículo deberás consultar a un distribuidor autorizado Volkswagen.",
    link: "https://www.vw.com.mx/es/autos-suvs/saveiro.html",
    exception: [
    ]
  },
  {
    name: "ID.3 Pure",
    line: "Pure",
    car: "id3_pure",
    disclaimer: "Das abgebildete Fahrzeug zeigt einen ID. 3 Pure aus dem Modelljahr 2022. Für genauere Beschreibungen wenden Sie sich an Ihren Volkswagen Partner.",
    link: "",
    exception: [
    ]
  },
  {
    name: "ID.3 Pro",
    line: "Pro",
    car: "id3_pro",
    disclaimer: "Das abgebildete Fahrzeug zeigt einen ID. 3 Pro aus dem Modelljahr 2022. Für genauere Beschreibungen wenden Sie sich an Ihren Volkswagen Partner.",
    link: "",
    exception: [
    ]
  },
  {
    name: "ID.3 Pro S",
    line: "Pro S",
    car: "id3_pro_s",
    disclaimer: "Das abgebildete Fahrzeug zeigt einen ID. 3 Pro S aus dem Modelljahr 2022. Für genauere Beschreibungen wenden Sie sich an Ihren Volkswagen Partner.",
    link: "",
    exception: [
    ]
  },
  {
    name: "ID.4 Pure",
    line: "Pure",
    car: "id4_pure",
    disclaimer: translate("disclaimer_id4_pure"),
    link: "",
    exception: [
    ]
  },
  {
    name: "ID.4 Pro",
    line: "Pro",
    car: "id4_pro",
    disclaimer: translate("disclaimer_id4_pro"),
    link: "",
    exception: [
    ]
  },
  {
    name: "ID.4 GTX",
    line: "GTX",
    car: "id4_gtx",
    disclaimer: translate("disclaimer_id4_gtx"),
    link: "",
    exception: [
    ]
  }
];

export interface ModelGroup {
  name:string;
  cars:string[];
}

/* Group multiple car lines together, for example:
 * - ID.4 Pure
 * - ID.4 Pro
 * - ID.4 GTX
 * into the group "ID.4".
 * This is used to group the car models in the model selection UI.
 */
const modelGroups:ModelGroup[] = [
  // SAVEIRO
  {
    name: "Saveiro",
    cars: [
      "saveiro"
    ]
  },
  // CROSS SPORT
  {
    name: "Cross Sport MY21",
    cars: [
      "crosssport-my21",
    ]
  },
  {
    name: "Cross Sport",
    cars: [
      "crosssport"
    ]
  },
  // VIRTUS
  {
    name: "Virtus",
    cars: [
      "virtus"
    ]
  },
  // JETTA
  {
    name: "Jetta",
    cars: [
      "jetta-my21"
    ]
  },
  {
    name: "Nuevo Jetta",
    cars: [
      "jetta"
    ]
  },
  // T-CROSS
  // {
  //   name: "T-Cross",
  //   cars: [
  //     "tcross-my22"
  //   ]
  // },
  {
    name: "Nuevo T-Cross",
    cars: [
      "tcross"
    ]
  },
  // NIVUS
  {
    name: "Nuevo Nivus",
    cars: [
      "nivus"
    ]
  },
  // TAOS
  {
    name: "Taos",
    cars: [
      "taos"
    ]
  },
  // TIGUAN
  {
    name: "Tiguan",
    cars: [
      "tiguan"
    ]
  },
  {
    name: "Nuevo Tiguan",
    cars: [
      "nuevotiguan"
    ]
  },
  // TERAMONT
  {
    name: "Teramont",
    cars: [
      "teramont"
    ]
  },
  // {
  //   name: "Polo",
  //   cars: [
  //     "polo"
  //   ]
  // },

  {
    name: "ID.4",
    cars: [
      "id4_pure",
      "id4_pro",
      "id4_gtx"
    ]
  },
  {
    name: "ID.3",
    cars: [
      "id3_pure",
      "id3_pro",
      "id3_pro_s"
    ]
  }
];

export class ModelLoadController {
  public static STATUS_LOADING: string = "loading";
  public static STATUS_LOADED: string = "loaded";
  public static STATUS_FAILED: string = "failed";
  public static EVENT_ANIMATION_START_STOP: string = "animation_start_stop";

  public static event: EventEmitter = new EventEmitter();

  private _currentModelName: string = "";
  private engine: RenderEngine;
  private modelController: ConfiguratorModel;
  private controller3D: Controller3D;
  private fileLoadProgress: FileLoadProgress
  private eventController:EventController;

  public get modelGroupList():ModelGroup[] {
    const list = JSON.parse(JSON.stringify(modelGroups));
    const hiddenCars = getWhiteLabel().hiddenCars;
    const availableCars = getWhiteLabel().availableCars.filter(e => !hiddenCars.includes(e));

    let i = 0;

    while (i < list.length) {
      const entry = list[i];
      entry.cars = entry.cars.filter(car => availableCars.indexOf(car) != -1);
      if (entry.cars.length == 0) {
        list.splice(i, 1);
      } else {
        i++;
      }
    }

    return list;
  }

  public get modelList(): string[] {
    const availableCars = getWhiteLabel().availableCars;

    return nameMap.map(e => e.car).filter(car => availableCars.includes(car));
  }

  public get defaultModelName(): string {
    return getWhiteLabel().defaultCar;
  }

  public get currentModelName(): string {
    return this._currentModelName;
  }

  public constructor(
    engine: RenderEngine,
    modelController: ConfiguratorModel,
    controller3D: Controller3D,
    eventController:EventController  
  ) {
    this.engine = engine;
    this.modelController = modelController;
    this.controller3D = controller3D;
    this.fileLoadProgress = new FileLoadProgress(engine.assetProvider);
    this.eventController = eventController;
  }

  public getModelGroupIndex(car:string):number {
    const groups = this.modelGroupList;

    for (let i = 0; i < groups.length; i++) {
      if (groups[i].cars.indexOf(car) != -1) {
        return i;
      }
    }

    throw `Model '${car}' is not in any model group.`;
  }

  public getCurrentModelGroup():ModelGroup { 
    return modelGroups.find(e => e.cars.includes(this.currentModelName));
  }

  public getModelName(): string {
    let s = "";
    let car = nameMap.find(e => e.car === this._currentModelName);
    if (car) {
      s = car.name;
    }
    return s;
  }

  public getModel(): any {
    let c = null;
    let car = nameMap.find(e => e.car === this._currentModelName);
    if (car) {
      c = car;
    }
    return c;
  }

  public getModelByIndex(index): any {
    let c = null;
    let car = nameMap.find(e => e.car === this.modelList[index]);
    if (car) {
      c = car;
    }
    return c;
  }

  public getModelByCode(code: string): any {
    let c = null;
    let i = -1;
    let car = nameMap.find(e => e.car === code);
    if (car) {
      c = car;
    }
    return c;
  }

  public getModelIndex(): number {
    let i = -1;
    this.modelList.forEach((e, index) => {
      if (e.toLowerCase() === this._currentModelName) {
        i = index
      }
    });

    return i;
  }

  public checkModelActive(name:string):boolean {
    return this.currentModelName === name;
  }
  
  public async getDefaultColor(modelName:string):Promise<number[]> {
    const failureColor = [0, 0, 0];

    if (!getWhiteLabel().availableCars.includes(modelName)) {
      console.error(`Unknown car model: ${modelName}`);
      return failureColor;
    }

    const config = await this.getCarJSON(modelName);
    const sceneConfig = this.getSceneAssetFromConfig(config);
    const exteriorStates = config.defaultState.exterior;

    let carpaintDefaultMaterial:IMaterial;

    if (exteriorStates) {
      /**
       * This car uses states to assign multiple materials to different material groups. 
       * To find the default color we find the default carpaint state and extract
       * the material that it assigns to the material group referenced in config.grouping.carpaint.
       */
      const stateName = exteriorStates.entries.includes(exteriorStates.default) ?
        exteriorStates.default : exteriorStates.entries[0];

      const transform = sceneConfig.transforms.find(t => t.name === stateName);

      if (transform) {
        const primaryColorMaterialGroup = sceneConfig.material_groups.find(
          mg => mg.name == config.groupings.carpaint);

        if (primaryColorMaterialGroup) {
          const primaryColorMaterialGroupState = transform.state.material_groups.find(
            mg => mg.id == primaryColorMaterialGroup.id);

          if (primaryColorMaterialGroupState) {
            carpaintDefaultMaterial = this.engine.assetProvider.getAsset(
              primaryColorMaterialGroupState.material_id) as IMaterial;
          }
        }
      }
    } else {
      /**
       * This is the normal case. The carpaint is assigned via a single material group
       * referenced in config.grouping.carpaint.
       * We extract the default state for this material group.
       */
      const carpaintGroup = sceneConfig.material_groups.find(mg => mg.name === config.groupings.carpaint);
      if (!carpaintGroup) {
        return failureColor;
      }
  
      const carpaintDefaultState = sceneConfig.default_state.material_groups.find(mg => mg.id === carpaintGroup.id);
      if (!carpaintDefaultState) {
        return failureColor;
      }
  
      carpaintDefaultMaterial = this.engine.assetProvider.getAssets().find(
        a => a.id == carpaintDefaultState.material_id) as IMaterial;    
    }

    if (!carpaintDefaultMaterial || carpaintDefaultMaterial.info.subType != MaterialTypes.Pbr) {
      return failureColor;
    }

    const props:any = carpaintDefaultMaterial.info.data;

    if (!props.hasOwnProperty("color")) {
      return failureColor;
    }

    return props.color.map(n => Math.pow(n, 1/2.2) * 255);
  }

  public async loadModel(modelName: string):Promise<void> {
    if (modelName == this._currentModelName) {
      return;
    }

    if (!getWhiteLabel().availableCars.includes(modelName)) {
      console.error(`Unknown car model: ${modelName}`);
      modelName = getWhiteLabel().defaultCar;
    }

    this._currentModelName = modelName;

    let config:CarModelConfig;

    try {
      config = await this.getCarJSON(modelName);
    } catch (error) {
      console.error("Error while loading car:", error)
      ModelLoadController.event.emit(ModelLoadController.STATUS_FAILED);
    }

    this.createMissingFeatureInformation(config);

    this.eventController.carModelConfig = config;

    // TODO: would be nicer to pass a progress callback into RenderEngine.loadConfigByName()
    const sceneConfig = this.getSceneAssetFromConfig(config);
    this.fileLoadProgress.getProgressForConfig(sceneConfig, (p) => {
      ModelLoadController.event.emit(ModelLoadController.STATUS_LOADING, p);
    });

    const scene = await this.engine.loadConfigByName(config.scene, this.eventController);

    // The selection groups NEED to be rebuild before emitting the event.
    await this.modelController.buildSelectionGroups(config);
    this.modelController.setAmbilightIntensity(0);

    ModelLoadController.event.emit(ModelLoadController.STATUS_LOADED, config);

    if (!this.controller3D.arController.xrEngine.active) {
      const DURATION = 5000;
      
      await this.controller3D.setCamera(CarModelCameraList.GroundHotspotLights, 0);
      this.controller3D.setCamera(CarModelCameraList.Orbit, DURATION);

      ModelLoadController.event.emit(ModelLoadController.EVENT_ANIMATION_START_STOP, true);

      try {
        doModelLoadIntroAnimation(config, scene, DURATION, this.controller3D.animationController, () => {
          ModelLoadController.event.emit(ModelLoadController.EVENT_ANIMATION_START_STOP, false);
        });
      } catch (ex) {
        ModelLoadController.event.emit(ModelLoadController.EVENT_ANIMATION_START_STOP, false);

        console.error("ModelLoadController: failed to initiate drive animation:", ex);
      }

      // wait for the first frame to be rendered.
      // this helps avoiding visible jumps in the camera or car animation.
      await new Promise<void>((resolve) => {
        this.engine.engine.services.render.onAfterRender.subscribe(async () => resolve());
      });
    } else {
      await this.controller3D.setCamera(CarModelCameraList.Orbit, 0);
    }
  }

  private createMissingFeatureInformation(config:CarModelConfig):void {
    if (!config.features) {
      config.features = {
      } as any; // will be filled with info below
    }
  
    const features = config.features;

    if (features.airbag === undefined) {
      features.airbag = true;
    }

    if (features.haveBackDoors === undefined) {
      features.haveBackDoors = true;
    }

    if (features.haveBackSeats === undefined) {
      features.haveBackSeats = true;
    }
  }

  private getCarJSON(modelName:string):Promise<CarModelConfig> {
    return Axios.get(`./${HASH}/cars/${modelName}.json`).then((response: AxiosResponse) => {
      return response.data as CarModelConfig;
    });
  }

  private getSceneAssetFromConfig(config:CarModelConfig):Configuration {
    return this.engine.assetProvider.getAssets().find(
      asset => asset.infoType === AssetInfoTypes.Scene && asset.info.name === config.scene).info.data;
  }
}